export const DEFAULT_COUNTRY = "Unspecified";
export const getCountry = (country) => country?.trim() || DEFAULT_COUNTRY;

// Sort only when requested, keeping equal years stable and missing years last.
export const sortByYaEarned = (rows, direction = "asc") => {
  const getYear = (value) => {
    if (value == null || String(value).trim() === "") return null;
    const year = Number(value);
    return Number.isFinite(year) ? year : null;
  };

  return [...(rows || [])].sort((a, b) => {
    const aYear = getYear(a.yaEarned);
    const bYear = getYear(b.yaEarned);
    if (aYear === null) return bYear === null ? 0 : 1;
    if (bYear === null) return -1;
    return direction === "desc" ? bYear - aYear : aYear - bYear;
  });
};

export const incomeTypes = [
  { value: "Interest", label: "Interest", group: "Income" },
  { value: "Rental", label: "Rental", group: "Income" },
  { value: "Royalty", label: "Royalty", group: "Income" },
  { value: "Dividend", label: "Dividend", group: "Income" },
  {
    value: "Trade / Branch Profits",
    label: "Trade / Branch Profits",
    group: "Income",
  },
];

export const countries = [
  { value: "Australia", label: "Australia", group: "Countries" },
  { value: "Bangladesh", label: "Bangladesh", group: "Countries" },
  { value: "Brunei", label: "Brunei", group: "Countries" },
  { value: "Cambodia", label: "Cambodia", group: "Countries" },
  { value: "China", label: "China", group: "Countries" },
  { value: "Hong Kong", label: "Hong Kong", group: "Countries" },
  { value: "India", label: "India", group: "Countries" },
  { value: "Indonesia", label: "Indonesia", group: "Countries" },
  { value: "Japan", label: "Japan", group: "Countries" },
  { value: "Malaysia", label: "Malaysia", group: "Countries" },
  { value: "Myanmar", label: "Myanmar", group: "Countries" },
  { value: "New Zealand", label: "New Zealand", group: "Countries" },
  { value: "Philippines", label: "Philippines", group: "Countries" },
  { value: "South Korea", label: "South Korea", group: "Countries" },
  { value: "Sri Lanka", label: "Sri Lanka", group: "Countries" },
  { value: "Taiwan", label: "Taiwan", group: "Countries" },
  { value: "Thailand", label: "Thailand", group: "Countries" },
  { value: "United Kingdom", label: "United Kingdom", group: "Countries" },
  { value: "United States", label: "United States", group: "Countries" },
  { value: "Vietnam", label: "Vietnam", group: "Countries" },
  { value: "Other", label: "Other", group: "Countries" },
];

export const foreignIncomeRowTemplate = {
  incomeType: "",
  country: "",
  yaEarned: "",
  unremittedIncomeBf: 0,
  currentYearIncome: 0,
  incomeReceived: 0,
  incomeUsedNotReceived: 0,
  allowableExpensesBf: 0,
  currentYearAllowableExpenses: 0,
  foreignTaxSuffered: 0,
  ftcClaim: false,
};

export const calculateTrackingRow = (row) => {
  const unremittedIncomeBf = row.unremittedIncomeBf || 0;
  const currentYearIncome = row.currentYearIncome || 0;
  const incomeReceived = row.incomeReceived || 0;
  const incomeUsedNotReceived = row.incomeUsedNotReceived || 0;
  const allowableExpensesBf = row.allowableExpensesBf || 0;
  const currentYearAllowableExpenses = row.currentYearAllowableExpenses || 0;

  // Calculate derived values
  const unremittedIncomeCf =
    unremittedIncomeBf +
    currentYearIncome -
    incomeReceived -
    incomeUsedNotReceived;
  const totalIncome = unremittedIncomeBf + currentYearIncome;
  const totalExpenses = allowableExpensesBf + currentYearAllowableExpenses;

  const shareOfExpensesRemitted =
    totalIncome > 0
      ? Math.round((incomeReceived / totalIncome) * totalExpenses)
      : 0;

  const shareOfExpensesNonRemitted =
    totalIncome > 0
      ? Math.round((incomeUsedNotReceived / totalIncome) * totalExpenses)
      : 0;

  const allowableExpensesCf =
    totalExpenses - shareOfExpensesRemitted - shareOfExpensesNonRemitted;
  const netIncomeReceived = incomeReceived - shareOfExpensesRemitted;

  // Calculate foreign tax shares
  const foreignTaxSufferedBf = row.foreignTaxSufferedBf || 0;
  const foreignTaxSuffered = row.foreignTaxSuffered || 0;
  const totalForeignTax = foreignTaxSufferedBf + foreignTaxSuffered;

  const shareOfForeignTaxSufferedRemitted =
    totalIncome > 0 && totalForeignTax > 0
      ? Math.round((incomeReceived / totalIncome) * totalForeignTax * 100) / 100
      : 0;

  const shareOfForeignTaxSufferedNonRemitted =
    totalIncome > 0 && totalForeignTax > 0
      ? Math.round(
          (incomeUsedNotReceived / totalIncome) * totalForeignTax * 100
        ) / 100
      : 0;

  // Calculate carry forward: total tax - remitted share - non-remitted share
  // This represents the foreign tax that remains unremitted
  const calculatedForeignTaxSufferedCf =
    totalForeignTax -
    shareOfForeignTaxSufferedRemitted -
    shareOfForeignTaxSufferedNonRemitted;

  return {
    ...row,
    country: getCountry(row.country),
    unremittedIncomeCf,
    shareOfExpensesRemitted,
    shareOfExpensesNonRemitted,
    allowableExpensesCf,
    netIncomeReceived,
    foreignTaxSufferedBf,
    foreignTaxSufferedCf: calculatedForeignTaxSufferedCf,
    foreignTaxSuffered,
    shareOfForeignTaxSufferedRemitted,
    shareOfForeignTaxSufferedNonRemitted,
  };
};

// Calculate all derived values for tracking schedule
export const calculateTrackingSchedule = (trackingData, rowId) => {
  return trackingData.map((row) => {
    if (rowId && rowId !== row.rowId) return row;

    return calculateTrackingRow(row);
  });
};

// Group data by income type for output mapping
export const groupByIncomeType = (data) => {
  const grouped = {};
  data.forEach((row) => {
    if (!grouped[row.incomeType]) {
      grouped[row.incomeType] = [];
    }
    grouped[row.incomeType].push(row);
  });
  return grouped;
};

// Group data by income type and country
export const groupByIncomeTypeAndCountry = (data) => {
  const grouped = {};
  data.forEach((row) => {
    const incomeType = row.incomeType || "";
    const country = getCountry(row.country);

    if (!grouped[incomeType]) {
      grouped[incomeType] = {};
    }
    if (!grouped[incomeType][country]) {
      grouped[incomeType][country] = [];
    }
    grouped[incomeType][country].push(row);
  });
  return grouped;
};

// Calculate totals by income type
export const calculateTotalsByIncomeType = (data) => {
  const grouped = groupByIncomeType(data);
  const totals = {};

  Object.keys(grouped).forEach((incomeType) => {
    const rows = grouped[incomeType];

    // Calculate share of foreign tax suffered for each row
    const totalShareOfForeignTax = rows.reduce((sum, row) => {
      return sum + (row.shareOfForeignTaxSufferedRemitted || 0);
    }, 0);

    totals[incomeType] = {
      totalNetIncome: rows.reduce(
        (sum, row) => sum + (row.netIncomeReceived || 0),
        0
      ),
      totalForeignTax: totalShareOfForeignTax,
      rowCount: rows.length,
    };
  });

  return totals;
};

// Calculate totals by income type and country
export const calculateTotalsByIncomeTypeAndCountry = (data) => {
  const grouped = groupByIncomeTypeAndCountry(data);
  const totals = {};

  Object.keys(grouped).forEach((incomeType) => {
    totals[incomeType] = {};

    Object.keys(grouped[incomeType]).forEach((country) => {
      const rows = grouped[incomeType][country];

      // Calculate totals for this income type and country combination
      const groupTotals = rows.reduce(
        (acc, row) => {
          return {
            unremittedIncomeBf:
              acc.unremittedIncomeBf + (row.unremittedIncomeBf || 0),
            currentYearIncome:
              acc.currentYearIncome + (row.currentYearIncome || 0),
            incomeReceived: acc.incomeReceived + (row.incomeReceived || 0),
            incomeUsedNotReceived:
              acc.incomeUsedNotReceived + (row.incomeUsedNotReceived || 0),
            unremittedIncomeCf:
              acc.unremittedIncomeCf + (row.unremittedIncomeCf || 0),
            allowableExpensesBf:
              acc.allowableExpensesBf + (row.allowableExpensesBf || 0),
            currentYearAllowableExpenses:
              acc.currentYearAllowableExpenses +
              (row.currentYearAllowableExpenses || 0),
            shareOfExpensesRemitted:
              acc.shareOfExpensesRemitted + (row.shareOfExpensesRemitted || 0),
            shareOfExpensesNonRemitted:
              acc.shareOfExpensesNonRemitted +
              (row.shareOfExpensesNonRemitted || 0),
            allowableExpensesCf:
              acc.allowableExpensesCf + (row.allowableExpensesCf || 0),
            netIncomeReceived:
              acc.netIncomeReceived + (row.netIncomeReceived || 0),
            foreignTaxSuffered:
              acc.foreignTaxSuffered + (row.foreignTaxSuffered || 0),
            foreignTaxSufferedBf:
              acc.foreignTaxSufferedBf + (row.foreignTaxSufferedBf || 0),
            foreignTaxSufferedCf:
              acc.foreignTaxSufferedCf + (row.foreignTaxSufferedCf || 0),
            shareOfForeignTaxSufferedRemitted:
              acc.shareOfForeignTaxSufferedRemitted +
              (row.shareOfForeignTaxSufferedRemitted || 0),
            shareOfForeignTaxSufferedNonRemitted:
              acc.shareOfForeignTaxSufferedNonRemitted +
              (row.shareOfForeignTaxSufferedNonRemitted || 0),
            // Keep for backward compatibility
            shareOfForeignTaxSuffered:
              acc.shareOfForeignTaxSuffered +
              (row.shareOfForeignTaxSuffered || 0),
          };
        },
        {
          unremittedIncomeBf: 0,
          currentYearIncome: 0,
          incomeReceived: 0,
          incomeUsedNotReceived: 0,
          unremittedIncomeCf: 0,
          allowableExpensesBf: 0,
          currentYearAllowableExpenses: 0,
          shareOfExpensesRemitted: 0,
          shareOfExpensesNonRemitted: 0,
          allowableExpensesCf: 0,
          netIncomeReceived: 0,
          foreignTaxSuffered: 0,
          foreignTaxSufferedBf: 0,
          foreignTaxSufferedCf: 0,
          shareOfForeignTaxSufferedRemitted: 0,
          shareOfForeignTaxSufferedNonRemitted: 0,
          shareOfForeignTaxSuffered: 0,
        }
      );

      totals[incomeType][country] = groupTotals;
    });
  });

  return totals;
};
